import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { push as navigateTo } from 'connected-react-router';
import { State } from 'reducers';
import { getBuildingByUrlSlug, getBuildingDevices, HasUrlSlug } from 'selectors/buildingsSelectors';
import { getDeviceTypeNamesLookup } from 'selectors/deviceTypesSelectors';
import { getDeviceTypesRequest } from 'actions/deviceTypeActions';
import { Switch, Route } from 'react-router-dom';
import { useParams } from 'util/reactRouterHooks';
import { getBuildingRequest } from 'actions/buildingActions';
import { DevicesPanel } from '../../devices/deviceList/DevicesPanel';
import { BuildingDeviceRouter } from 'ui/building/device/BuildingDeviceRouter';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { DeviceId } from 'shared/models';
import { AddDevicesDialog } from 'ui/devices/addDevicesDialog/AddDevicesDialog';

const ONE_MINUTE = 60000;

export const BuildingDevicesPage = () => {
    const params = useParams<HasUrlSlug>();
    const { urlSlug } = params;

    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const buildingId = building && building.id;

    const { hasPermission } = useSession();
    const devices = useSelector((state: State) => (buildingId && getBuildingDevices(state, { buildingId }) || {}));
    const deviceTypeNames = useSelector(getDeviceTypeNamesLookup);

    const [ addDialogVisible, setAddDialogVisible ] = useState(false);

    const dispatch = useDispatch();

    const handleClickAdd = useCallback(() => {
        setAddDialogVisible(true);
    }, [ setAddDialogVisible ]);

    const handleClickSchedule = useCallback((id: DeviceId) => dispatch(navigateTo(`/buildings/${urlSlug}/devices/${id}/schedule`)), [ dispatch, urlSlug ]);
    const loadData = useCallback(() => dispatch(getBuildingRequest(buildingId!)), [ dispatch, buildingId ]);

    useEffect(() => {
        // Load device types if we haven't already
        const deviceTypeCount = Object.keys(deviceTypeNames).length;
        if (deviceTypeCount === 0) {
            dispatch(getDeviceTypesRequest());
        }

        // Reload device status every minute
        const intervalId = setInterval(loadData, ONE_MINUTE);
        return () => clearInterval(intervalId);
    }, [ buildingId, deviceTypeNames, dispatch, loadData ]);

    return (
        <div id="building-devices-page" className="page">
            { hasPermission(buildingId, Permission.CreateDevice) && addDialogVisible &&
                <AddDevicesDialog
                    visible={ addDialogVisible }
                    buildingId={ buildingId! }
                    onClose={ () => setAddDialogVisible(false) }
                />
            }
            <Switch>
                <Route path="/buildings/:urlSlug/devices/:deviceId" component={ BuildingDeviceRouter }/>
            </Switch>
            <DevicesPanel
                devices={ devices }
                onClickAdd={ handleClickAdd }
                onClickSchedule={ handleClickSchedule }
            />
        </div>
    );
};
